import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { Plus, Loader2, Trash2, Wallet } from 'lucide-react';
import { toast } from 'sonner';

/**
 * ExpensesPanel — Track recurring subscriptions & other business expenses.
 * Displays totals per month & per year for quick cash-flow visibility.
 */
export default function ExpensesPanel() {
  const [items, setItems] = useState([]);
  const [totals, setTotals] = useState({ monthly: 0, yearly: 0 });
  const [loading, setLoading] = useState(true);
  const [showAdd, setShowAdd] = useState(false);
  const [form, setForm] = useState({ name: '', provider: '', amount_eur: '', frequency: 'monthly', category: 'subscription', notes: '' });
  const [saving, setSaving] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const r = await axios.get('/api/admin-ext/expenses');
      setItems(r.data?.items || []);
      setTotals({ monthly: r.data?.monthly_total_eur || 0, yearly: r.data?.yearly_total_eur || 0 });
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const add = async () => {
    if (!form.name.trim() || !Number(form.amount_eur)) { toast.error('Nom et montant obligatoires'); return; }
    setSaving(true);
    try {
      await axios.post('/api/admin-ext/expenses', { ...form, amount_eur: Number(form.amount_eur) });
      setForm({ name: '', provider: '', amount_eur: '', frequency: 'monthly', category: 'subscription', notes: '' });
      setShowAdd(false);
      await load();
      toast.success('Dépense ajoutée');
    } catch { toast.error('Erreur'); } finally { setSaving(false); }
  };

  const del = async (id) => {
    if (!window.confirm('Supprimer cette dépense ?')) return;
    try {
      await axios.delete(`/api/admin-ext/expenses/${id}`);
      await load();
    } catch { toast.error('Erreur'); }
  };

  return (
    <div className="mb-4 p-4 rounded-2xl bg-gradient-to-br from-sky-500/10 to-indigo-500/10 border border-sky-500/30" data-testid="expenses-panel">
      <div className="flex items-start gap-3 mb-3">
        <Wallet className="w-5 h-5 text-sky-300 shrink-0 mt-0.5" />
        <div className="flex-1 min-w-0">
          <h4 className="font-bold text-sky-200 text-sm">Dépenses & abonnements</h4>
          <p className="text-[11px] text-sky-200/70 mt-0.5 leading-snug">
            Suivez vos frais récurrents pour piloter votre trésorerie.
          </p>
        </div>
      </div>

      <div className="grid grid-cols-2 gap-2 mb-3">
        <div className="p-2 rounded-xl bg-black/30 border border-sky-500/20">
          <div className="text-[10px] text-sky-200/60">Total mensuel</div>
          <div className="text-sm font-bold text-white">{totals.monthly.toFixed(2)} €</div>
        </div>
        <div className="p-2 rounded-xl bg-black/30 border border-sky-500/20">
          <div className="text-[10px] text-sky-200/60">Total annuel</div>
          <div className="text-sm font-bold text-white">{totals.yearly.toFixed(2)} €</div>
        </div>
      </div>

      {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : (
        <div className="space-y-1 max-h-40 overflow-y-auto mb-2">
          {items.length === 0 ? <div className="text-[11px] text-sky-200/50">Aucune dépense.</div> : items.map(i => (
            <div key={i.id} className="p-2 rounded-lg bg-black/30 border border-sky-500/20 flex items-center justify-between gap-2">
              <div className="min-w-0 flex-1">
                <div className="text-xs font-semibold text-sky-100 truncate">{i.name} <span className="text-sky-200/50">· {i.provider}</span></div>
                <div className="text-[10px] text-sky-200/60">{i.amount_eur.toFixed(2)} € / {i.frequency === 'monthly' ? 'mois' : i.frequency === 'yearly' ? 'an' : 'ponctuel'} · {i.category}</div>
              </div>
              <button onClick={() => del(i.id)} className="p-1.5 rounded-full bg-red-500/20" data-testid={`expense-del-${i.id}`}>
                <Trash2 className="w-3 h-3 text-red-300" />
              </button>
            </div>
          ))}
        </div>
      )}

      {!showAdd ? (
        <button onClick={() => setShowAdd(true)} className="w-full py-2 rounded-full bg-gradient-to-r from-sky-500 to-indigo-500 font-semibold text-xs flex items-center justify-center gap-1" data-testid="expenses-add-open">
          <Plus className="w-3.5 h-3.5" /> Nouvelle dépense
        </button>
      ) : (
        <div className="space-y-2 p-3 rounded-xl bg-black/40 border border-sky-500/30">
          <div className="grid grid-cols-2 gap-2">
            <input value={form.name} onChange={e => setForm({...form, name: e.target.value})} placeholder="Nom (ex: OpenAI)" className="bg-black/40 border border-sky-500/30 rounded-full px-3 py-1.5 text-xs" data-testid="expense-name" />
            <input value={form.provider} onChange={e => setForm({...form, provider: e.target.value})} placeholder="Fournisseur" className="bg-black/40 border border-sky-500/30 rounded-full px-3 py-1.5 text-xs" data-testid="expense-provider" />
          </div>
          <div className="grid grid-cols-3 gap-2">
            <input type="number" step="0.01" value={form.amount_eur} onChange={e => setForm({...form, amount_eur: e.target.value})} placeholder="€" className="bg-black/40 border border-sky-500/30 rounded-full px-3 py-1.5 text-xs" data-testid="expense-amount" />
            <select value={form.frequency} onChange={e => setForm({...form, frequency: e.target.value})} className="bg-black/40 border border-sky-500/30 rounded-full px-3 py-1.5 text-xs" data-testid="expense-frequency">
              <option value="monthly" className="bg-gray-900">Mensuel</option>
              <option value="yearly" className="bg-gray-900">Annuel</option>
              <option value="one_off" className="bg-gray-900">Ponctuel</option>
            </select>
            <select value={form.category} onChange={e => setForm({...form, category: e.target.value})} className="bg-black/40 border border-sky-500/30 rounded-full px-3 py-1.5 text-xs" data-testid="expense-category">
              <option value="subscription" className="bg-gray-900">Abonnement</option>
              <option value="api" className="bg-gray-900">API / IA</option>
              <option value="infra" className="bg-gray-900">Hébergement</option>
              <option value="other" className="bg-gray-900">Autre</option>
            </select>
          </div>
          <div className="flex gap-2">
            <button onClick={() => setShowAdd(false)} className="flex-1 py-2 rounded-full bg-white/10 text-xs" data-testid="expense-cancel">Annuler</button>
            <button onClick={add} disabled={saving} className="flex-1 py-2 rounded-full bg-gradient-to-r from-sky-500 to-indigo-500 font-semibold text-xs disabled:opacity-50" data-testid="expense-save">
              {saving ? 'Enregistrement…' : 'Enregistrer'}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
